'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';

interface NavItem {
  label: string;
  href: string;
  icon: string;
  badge?: number;
}

const navItems: NavItem[] = [
  { label: 'Mission Control', href: '/', icon: 'HomeIcon' },
  { label: 'Dashboard', href: '/profile-dashboard', icon: 'ChartBarIcon' },
  { label: 'Food Log', href: '/food-logging', icon: 'ClipboardDocumentListIcon' },
  { label: 'The Gap', href: '/the-gap', icon: 'ArrowTrendingDownIcon' },
  { label: 'The Arena', href: '/the-forge-arena', icon: 'TrophyIcon' },
  { label: 'The Anvil', href: '/the-anvil', icon: 'ShieldCheckIcon', badge: 1 },
  { label: 'Behavior Intel', href: '/behavior-intelligence', icon: 'BeakerIcon' },
];

interface SidebarProps {
  activeRoute: string;
}

export default function Sidebar({ activeRoute }: SidebarProps) {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <>
      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/60 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Mobile toggle */}
      <button
        className="fixed top-4 left-4 z-50 lg:hidden card-forge p-2 rounded-md"
        onClick={() => setMobileOpen(!mobileOpen)}
        aria-label="Toggle navigation"
      >
        <Icon name="Bars3Icon" size={20} className="text-foreground" />
      </button>

      {/* Sidebar */}
      <aside
        className={`
          fixed lg:static inset-y-0 left-0 z-50 flex flex-col
          bg-card border-r border-border
          transition-all duration-300 ease-in-out
          ${collapsed ? 'w-16' : 'w-60'}
          ${mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
      >
        {/* Logo */}
        <div className={`flex items-center gap-3 p-4 border-b border-border ${collapsed ? 'justify-center' : ''}`}>
          <div className="flex items-center gap-2">
            <AppLogo size={32} />
            {!collapsed && (
              <span className="font-black text-lg tracking-widest text-foreground uppercase">
                FORGE
              </span>
            )}
          </div>
        </div>

        {/* Profile selector */}
        {!collapsed && (
          <div className="px-3 py-3 border-b border-border">
            <div className="text-forge-label mb-2">Active Profile</div>
            <div className="flex gap-2">
              <button className="flex-1 py-1.5 text-xs font-bold tracking-wider rounded border border-primary/40 text-primary bg-primary/10 transition-all hover:bg-primary/20">
                THEO
              </button>
              <button className="flex-1 py-1.5 text-xs font-bold tracking-wider rounded border border-border text-muted-foreground transition-all hover:border-accent/40 hover:text-accent">
                TRACY
              </button>
            </div>
          </div>
        )}

        {/* Nav items */}
        <nav className="flex-1 p-2 space-y-0.5 overflow-y-auto scrollbar-forge">
          {navItems.map((item) => {
            const isActive = activeRoute === item.href;
            return (
              <Link
                key={`nav-${item.href}`}
                href={item.href}
                className={`
                  group flex items-center gap-3 px-3 py-2.5 rounded-md
                  transition-all duration-150
                  ${isActive
                    ? 'bg-primary/10 text-primary border border-primary/20' :'text-muted-foreground hover:bg-card-elevated hover:text-foreground border border-transparent'
                  }
                  ${collapsed ? 'justify-center' : ''}
                `}
                title={collapsed ? item.label : undefined}
                onClick={() => setMobileOpen(false)}
              >
                <Icon
                  name={item.icon as Parameters<typeof Icon>[0]['name']}
                  size={18}
                  className={isActive ? 'text-primary' : 'text-muted-foreground group-hover:text-foreground'}
                />
                {!collapsed && (
                  <>
                    <span className="text-sm font-medium flex-1">{item.label}</span>
                    {item.badge && (
                      <span className="w-5 h-5 rounded-full bg-accent/20 text-accent text-2xs flex items-center justify-center font-bold">
                        {item.badge}
                      </span>
                    )}
                  </>
                )}
              </Link>
            );
          })}
        </nav>

        {/* Bottom: collapse toggle + live indicator */}
        <div className="p-3 border-t border-border space-y-2">
          {!collapsed && (
            <div className="flex items-center gap-2 px-2">
              <span className="w-2 h-2 rounded-full bg-primary animate-pulse-green" />
              <span className="text-2xs text-muted-foreground font-mono">LIVE · 08 JUN 2026</span>
            </div>
          )}
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="w-full flex items-center justify-center gap-2 py-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-card-elevated transition-all"
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <Icon
              name={collapsed ? 'ChevronRightIcon' : 'ChevronLeftIcon'}
              size={16}
            />
            {!collapsed && <span className="text-xs font-medium">Collapse</span>}
          </button>
        </div>
      </aside>
    </>
  );
}